import { useCallback, useEffect, useState } from 'react'
import type { SkillDefinition } from '../types'
import { getSkillService, withRetry } from '../services'

interface UseSkillsResult {
  /**
   * `null` while it is still being found out.
   *
   * Not an empty array, which is a different fact and draws a different thing.
   * Every time these two have been collapsed into one value on this project it
   * has cost an evening: an empty filter that looked like a working one, a
   * "No projects yet" that had never asked, a panel that offered to connect an
   * account that was already connected.
   */
  skills: SkillDefinition[] | null
  failed: boolean
  reload: () => void
}

/**
 * The platform's skills, from the platform's own list.
 *
 * Retried, because it runs once and the screens that use it can live for
 * hours — the same lesson the Miro panel's filter taught the hard way.
 */
export function useSkills(): UseSkillsResult {
  const [skills, setSkills] = useState<SkillDefinition[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setFailed(false)
    withRetry(() => getSkillService().listSkills())
      .then((rows) => {
        if (!cancelled) setSkills(rows)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  const reload = useCallback(() => setAttempt((n) => n + 1), [])

  return { skills, failed, reload }
}
